import GradientShareButton from "../../../../shared/components/GradientShareButton";
import InfoTooltip from "../../../../shared/components/InfoTooltip";
import UserAvatar from "../../../../shared/components/UserAvatar";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import type { Club } from "../../../clubs/shared/types/club";
import type {
  OpenMatchItem,
  OpenMatchParticipantStatus,
} from "../../shared/types/matches";
import {
  buildOpenMatchMapUrl,
  formatOpenMatchCategory,
  formatOpenMatchGenderPreference,
  formatOpenMatchPerPlayerPrice,
  formatOpenMatchScheduleRange,
  formatOpenMatchSideRequirement,
  isOpenMatchFinished,
  resolveOpenMatchCategoryClass,
  resolveOpenMatchDisplayTitle,
  resolveOpenMatchDurationMinutes,
} from "../../shared/utils/openMatchPresentation";

type MatchCardProps = {
  match: OpenMatchItem;
  club?: Club | null;
  onShare: (match: OpenMatchItem) => void;
  canEdit?: boolean;
  onEdit?: (match: OpenMatchItem) => void;
  onAddParticipant?: (match: OpenMatchItem) => void;
  onAcceptInvite?: (match: OpenMatchItem) => void;
  onDeclineInvite?: (match: OpenMatchItem) => void;
  inviteActionRunning?: boolean;
  onConfirmMatch?: (match: OpenMatchItem) => void;
  confirmActionRunning?: boolean;
  onCancelMatch?: (match: OpenMatchItem) => void;
  cancelActionRunning?: boolean;
};

type MatchSlotPreview = {
  id: string;
  name: string;
  userId?: number | null;
  avatarUrl?: string | null;
  sex?: "male" | "female" | "other" | "" | null;
  status: OpenMatchParticipantStatus | "organizer" | "empty";
};

function normalizeMatchSlotSex(value?: string | null): MatchSlotPreview["sex"] {
  if (value === "male" || value === "female" || value === "other") return value;
  return "";
}

function MatchSlotStatusIcon({
  status,
  label,
}: {
  status: MatchSlotPreview["status"];
  label: string;
}) {
  if (status === "pending" || status === "invited") {
    return (
      <span
        className="match-card__slot-status match-card__slot-status--pending material-symbols-outlined"
        aria-label={label}
        title={label}
      >
        schedule
      </span>
    );
  }
  if (status === "confirmed" || status === "organizer") {
    return (
      <span
        className="match-card__slot-status match-card__slot-status--confirmed material-symbols-outlined"
        aria-label={label}
        title={label}
      >
        check_circle
      </span>
    );
  }
  return null;
}

export default function MatchCard({
  match,
  club = null,
  onShare,
  canEdit = false,
  onEdit,
  onAddParticipant,
  onAcceptInvite,
  onDeclineInvite,
  inviteActionRunning = false,
  onConfirmMatch,
  confirmActionRunning = false,
  onCancelMatch,
  cancelActionRunning = false,
}: MatchCardProps) {
  const texts = siteTexts.openMatches;
  const occupiedParticipants = (match.participants || []).filter(
    (participant) =>
      ["confirmed", "pending", "invited"].includes(participant.status),
  );
  const filledSlots =
    (match.organizerPlays ? 1 : 0) + occupiedParticipants.length;
  const availableSlots = Math.max(
    0,
    Number(match.totalPlayers || 0) - filledSlots,
  );
  const durationMinutes = resolveOpenMatchDurationMinutes(match);
  const estimatedValue = formatOpenMatchPerPlayerPrice(match);
  const hasVariablePerPlayerPrice =
    match.priceMode !== "player" && Number(match.totalPlayers || 0) > 4;
  const uniqueCategories = Array.from(new Set((match.categories || []).filter(Boolean)));
  const title = resolveOpenMatchDisplayTitle(match);
  const mapUrl = buildOpenMatchMapUrl(match);
  const coverImage = club?.coverImageUrl || club?.logoUrl || "";
  const isCanceled = match.status === "canceled";
  const isFinished = !isCanceled && isOpenMatchFinished(match);
  const isMatchConfirmed = Boolean(match.confirmedAt);
  const confirmedFilledSlots =
    (match.organizerPlays ? 1 : 0) +
    Number(match.participantSummary?.confirmed || 0);
  const canConfirmMatch =
    Boolean(canEdit && onConfirmMatch) &&
    !isCanceled &&
    !isFinished &&
    !isMatchConfirmed;
  const isFull =
    match.status === "full" &&
    confirmedFilledSlots >= Number(match.totalPlayers || 0);
  const canReviewInvite =
    !isCanceled &&
    !isFinished &&
    match.currentUserParticipant?.status === "invited" &&
    Boolean(onAcceptInvite || onDeclineInvite);
  const statusLabel =
    isCanceled
      ? texts.card.statusCanceled
      : isFinished
        ? texts.card.statusFinished
      : isMatchConfirmed
        ? texts.card.statusConfirmed
      : isFull
      ? texts.card.statusFull
      : match.approvalRequired
        ? texts.card.statusApproval
        : texts.card.statusOpen;
  const statusClassName =
    isCanceled
      ? "match-card__status-badge match-card__status-badge--canceled"
      : isFinished
        ? "match-card__status-badge match-card__status-badge--finished"
      : isMatchConfirmed
        ? "match-card__status-badge match-card__status-badge--confirmed"
      : isFull
      ? "match-card__status-badge match-card__status-badge--full"
      : match.approvalRequired
        ? "match-card__status-badge match-card__status-badge--approval"
        : "match-card__status-badge match-card__status-badge--open";
  const slots: MatchSlotPreview[] = [
    ...(match.organizerPlays
      ? [
          {
            id: "organizer",
            name: match.organizer.name,
            userId: match.organizer.id,
            avatarUrl: match.organizer.avatarUrl || "",
            sex: "" as const,
            status: "organizer" as const,
          },
        ]
      : []),
    ...occupiedParticipants.map((participant) => ({
      id: `participant-${participant.id}`,
      name: participant.participantName || texts.detail.participantFallback,
      userId: participant.userId,
      avatarUrl: participant.avatarUrl || "",
      sex: normalizeMatchSlotSex(participant.sex),
      status: participant.status,
    })),
  ].slice(0, Math.max(0, Number(match.totalPlayers || 0)));
  const emptySlots = Math.max(
    0,
    Number(match.totalPlayers || 0) - slots.length,
  );
  const slotPreviews = [
    ...slots,
    ...Array.from({ length: emptySlots }, (_, index) => ({
      id: `empty-${index}`,
      name: texts.card.emptySlot,
      status: "empty" as const,
    })),
  ];

  return (
    <article className="match-card">
      <div className="match-card__layout">
        <div className="match-card__media">
          {coverImage ? (
            <img src={coverImage} alt="" loading="lazy" />
          ) : (
            <div className="match-card__media-placeholder" aria-hidden="true">
              <span className="material-symbols-outlined">sports_tennis</span>
            </div>
          )}
        </div>

        <div className="match-card__primary">
          <div className="match-card__topline">
            <div className="match-card__heading">
              <span className="match-card__title">{title}</span>
              <span className="match-card__city">
                <span className="material-symbols-outlined" aria-hidden="true">
                  location_on
                </span>
                {match.city}
                {match.state ? ` • ${match.state}` : ""}
              </span>
            </div>
          </div>

          <div className="match-card__schedule-list">
            <div className="match-card__schedule-item">
              <span className="material-symbols-outlined" aria-hidden="true">
                calendar_month
              </span>
              <strong>{formatOpenMatchScheduleRange(match)}</strong>
            </div>
            <div className="match-card__schedule-item">
              <span className="material-symbols-outlined" aria-hidden="true">
                timer
              </span>
              <span>{durationMinutes} min</span>
            </div>
          </div>

          <div className="match-card__organizer">
            <UserAvatar
              name={match.organizer.name}
              avatarUrl={match.organizer.avatarUrl || undefined}
              size={44}
            />
            <div className="match-card__organizer-copy">
              <small>{texts.card.organizer}</small>
              <strong>{match.organizer.name}</strong>
              <span>
                {match.organizerPlays
                  ? texts.card.organizerPlays
                  : texts.card.organizerOnly}
              </span>
            </div>
          </div>
        </div>

        <div className="match-card__meta-panel">
          <div className="match-card__chip-grid">
            <div className="match-card__chip-block">
              <small>{texts.card.categories}</small>
              <span className="match-category-badges">
                {uniqueCategories.map((category) => (
                  <span
                    key={`match-card-category-${match.id}-${category}`}
                    className={`match-card__chip match-card__chip--category match-category-badge match-category-badge--${resolveOpenMatchCategoryClass(category)}`}
                  >
                    {formatOpenMatchCategory(category)}
                  </span>
                ))}
              </span>
            </div>
            <div className="match-card__chip-block">
              <small>{texts.card.gender}</small>
              <span className="match-card__chip match-card__chip--purple">
                {formatOpenMatchGenderPreference(match.genderPreference)}
              </span>
            </div>
            <div className="match-card__chip-block">
              <small>{texts.card.side}</small>
              <span className="match-card__chip match-card__chip--pink">
                {formatOpenMatchSideRequirement(match)}
              </span>
            </div>
            <div className="match-card__chip-block">
              <small>{texts.detail.club}</small>
              <strong>{match.clubName}</strong>
            </div>
          </div>
        </div>

        <div className="match-card__availability">
          <span className={statusClassName}>{statusLabel}</span>
          <div className="match-card__availability-box">
            <span className="material-symbols-outlined" aria-hidden="true">
              group
            </span>
            <div>
              <strong>
                {filledSlots} de {match.totalPlayers} {texts.card.playersLabel}
              </strong>
              <span>
                {isFinished
                  ? texts.detail.finishedMatchNotice
                  : isMatchConfirmed
                    ? texts.detail.confirmedMatchNotice
                    : availableSlots > 0
                      ? `${availableSlots} ${texts.card.availableSlots}`
                      : texts.card.waitingListClosed}
              </span>
            </div>
          </div>
          {canReviewInvite && (
            <div className="match-card__invite-actions">
              <button
                type="button"
                className="match-card__invite-action match-card__invite-action--accept"
                onClick={() => onAcceptInvite?.(match)}
                disabled={inviteActionRunning}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  check
                </span>
                {texts.detail.acceptInvite}
              </button>
              <button
                type="button"
                className="match-card__invite-action match-card__invite-action--decline"
                onClick={() => onDeclineInvite?.(match)}
                disabled={inviteActionRunning}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  close
                </span>
                {texts.detail.declineInvite}
              </button>
            </div>
          )}
          <div
            className="match-card__slots-preview"
            aria-label={texts.card.slotsPreview}
          >
            {slotPreviews.map((slot) =>
              slot.status === "empty" ? (
                !isCanceled &&
                !isFinished &&
                !isMatchConfirmed &&
                canEdit &&
                onAddParticipant ? (
                  <button
                    key={slot.id}
                    type="button"
                    className="match-card__slot match-card__slot--empty match-card__slot--clickable"
                    title={texts.detail.addParticipant}
                    aria-label={texts.detail.addParticipant}
                    onClick={() => onAddParticipant(match)}
                  />
                ) : (
                  <span
                    key={slot.id}
                    className="match-card__slot match-card__slot--empty"
                    title={texts.card.emptySlot}
                    aria-label={texts.card.emptySlot}
                  />
                )
              ) : (
                slot.userId ? (
                  <a
                    key={slot.id}
                    className={`match-card__slot match-card__slot--filled match-card__slot--clickable${
                      slot.status === "pending" || slot.status === "invited"
                        ? " match-card__slot--pending"
                        : ""
                    }`}
                    href={`#/jogadores/${slot.userId}`}
                    title={
                      slot.status === "pending" || slot.status === "invited"
                        ? `${slot.name} · ${texts.card.pendingSlot}`
                        : slot.name
                    }
                    aria-label={slot.name}
                  >
                    <UserAvatar
                      name={slot.name}
                      avatarUrl={slot.avatarUrl || undefined}
                      sex={slot.sex || undefined}
                      size={34}
                    />
                    <MatchSlotStatusIcon
                      status={slot.status}
                      label={
                        slot.status === "pending" || slot.status === "invited"
                          ? texts.card.pendingSlot
                          : texts.detail.participantStatuses.confirmed
                      }
                    />
                  </a>
                ) : (
                  <span
                    key={slot.id}
                    className={`match-card__slot match-card__slot--filled${
                      slot.status === "pending" || slot.status === "invited"
                        ? " match-card__slot--pending"
                        : ""
                    }`}
                    title={
                      slot.status === "pending" || slot.status === "invited"
                        ? `${slot.name} · ${texts.card.pendingSlot}`
                        : slot.name
                    }
                  >
                    <UserAvatar
                      name={slot.name}
                      avatarUrl={slot.avatarUrl || undefined}
                      sex={slot.sex || undefined}
                      size={34}
                    />
                    <MatchSlotStatusIcon
                      status={slot.status}
                      label={
                        slot.status === "pending" || slot.status === "invited"
                          ? texts.card.pendingSlot
                          : texts.detail.participantStatuses.confirmed
                      }
                    />
                  </span>
                )
              ),
            )}
          </div>
          <div className="match-card__price">
            <span className="material-symbols-outlined" aria-hidden="true">
              sell
            </span>
            <div>
              <span className="match-card__price-value">
                <strong>{estimatedValue}</strong>
                {hasVariablePerPlayerPrice ? (
                  <InfoTooltip
                    ariaLabel={texts.card.priceInfoAriaLabel}
                    content={texts.card.priceInfo}
                  />
                ) : null}
              </span>
              <span>{texts.card.estimatedPerPlayer}</span>
            </div>
          </div>
        </div>

        <div className="match-card__actions-panel">
          <a className="primary-button" href={`#/partidas/${match.id}`}>
            {texts.actions.viewDetails}
          </a>
          {!isCanceled ? (
            <>
              <GradientShareButton onClick={() => onShare(match)}>
                {texts.actions.share}
              </GradientShareButton>
              {mapUrl ? (
                <a
                  className="ghost-button"
                  href={mapUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {texts.actions.openMap}
                </a>
              ) : null}
              {canEdit && onEdit && !isFinished && !isMatchConfirmed ? (
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => onEdit(match)}
                >
                  {texts.actions.edit}
                </button>
              ) : null}
              {canEdit && onConfirmMatch && !isFinished && !isMatchConfirmed ? (
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => onConfirmMatch(match)}
                  disabled={!canConfirmMatch || confirmActionRunning}
                  title={
                    canConfirmMatch
                      ? undefined
                      : texts.detail.confirmMatchBlocked
                  }
                >
                  {confirmActionRunning
                    ? texts.detail.confirmMatchLoading
                    : texts.detail.confirmMatch}
                </button>
              ) : null}
              {canEdit && onCancelMatch && !isFinished ? (
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => onCancelMatch(match)}
                  disabled={cancelActionRunning}
                >
                  {cancelActionRunning
                    ? texts.form.cancelMatchSubmitting
                    : texts.form.cancelMatch}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}
